import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { de, hasAtLeastRole } from '@fsv/core';
import { readLastTenant, writeLastTenant } from './auth/lastTenant';
import { startDemo } from './auth/sessionStore';
import { AppShell } from './components/AppShell';
import { TenantProvider, useSession, useSessionState, useTenant } from './data/hooks';
import { tx } from '@fsv/data';
import { demoEntryFromUrl } from './lib/demoEntry';
import { findMembership, legacyRedirect, resolveHome, tenantPath } from './lib/tenantRouting';
import { Footer } from './components/Footer';
import { PartMissing } from './components/UpdateBand';
import { HelpScreen } from './help/HelpScreen';
import { FleetScreen } from './screens/FleetScreen';
import { InboxScreen } from './screens/InboxScreen';
import { ImpressumScreen, LoginScreen, PrivacyScreen, TermsScreen } from './screens/LoginScreen';
import { PlanScreen } from './screens/PlanScreen';
import { TenantPrivacyScreen } from './screens/PrivacyScreen';
import { MyReportsScreen, ProfileScreen } from './screens/ProfileScreen';
import { SearchScreen } from './screens/SearchScreen';
import { ErrorState, Loading } from './screens/states';
import { NoTenantScreen, TenantPickerScreen } from './screens/TenantPickerScreen';

// Detail, admin and platform are not needed for the first screen at the aircraft; they load on demand.
const partMissing = () => ({ default: PartMissing });
// A tab older than the deployment asks for chunk names the server no longer has: say so (PartMissing) instead of a blank page.
const DamageDetailScreen = lazy(() => import('./screens/DamageDetailScreen').then((m) => ({ default: m.DamageDetailScreen }), partMissing));
const AdminRoutes = lazy(() => import('./screens/admin/AdminRoutes').catch(partMissing));
const PlatformRoutes = lazy(() => import('./screens/platform/PlatformRoutes').catch(partMissing));
// The sign-up of a club is public and needed by few: its own chunk (0024).
const SignupScreen = lazy(() => import('./screens/signup/SignupScreen').then((m) => ({ default: m.SignupScreen }), partMissing));

function RequireSession({ children }: { children: ReactNode }) {
  const state = useSessionState();
  const location = useLocation();
  if (state.status === 'loading') return <Loading />;
  if (state.status === 'signed-out') return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <>{children}</>;
}

function NotFound() {
  return (
    <div className="page page--narrow">
      <ErrorState error={new Error(de.errors.notFound)} />
    </div>
  );
}

/** Hides what the role in the active tenant may not use. The API checks again; this is convenience, not security. */
function RequireRole({ min, children }: { min: 'editor' | 'admin'; children: ReactNode }) {
  const { role } = useTenant();
  if (!hasAtLeastRole(role, min)) {
    return (
      <div className="page page--narrow">
        <ErrorState error={new Error(de.auth.forbidden)} />
      </div>
    );
  }
  return <>{children}</>;
}

function RequirePlatformManager({ children }: { children: ReactNode }) {
  const { me } = useSession();
  return me.isPlatformManager ? <>{children}</> : <NotFound />;
}

/** `/`: last used tenant, the only one, the picker, or the platform area (lib/tenantRouting). */
function HomeRedirect() {
  const { me } = useSession();
  const home = resolveHome(me, readLastTenant());
  switch (home.kind) {
    case 'tenant':
      return <Navigate to={tenantPath(home.slug)} replace />;
    case 'picker':
      return <Navigate to="/vereine" replace />;
    case 'platform':
      return <Navigate to="/plattform" replace />;
    case 'none':
      return (
        <AppShell>
          <NoTenantScreen />
        </AppShell>
      );
  }
}

/** Bookmarks from before tenancy (/flugzeug/…, /schaden/…). */
function LegacyRedirect() {
  const { me } = useSession();
  const location = useLocation();
  const target = legacyRedirect(location.pathname, location.search, me, readLastTenant());
  return <Navigate to={target ?? '/'} replace />;
}

/**
 * /v/:slug/… — the active tenant. A slug without an active membership shows
 * the ordinary not-found page, exactly like the API's 404 for a foreign
 * tenant: whether the club exists is not revealed.
 */
function TenantRoute() {
  const { slug } = useParams();
  const { me } = useSession();
  const membership = findMembership(me, slug);
  useEffect(() => {
    if (membership) writeLastTenant(membership.slug);
  }, [membership]);
  if (!membership) {
    return (
      <AppShell>
        <NotFound />
      </AppShell>
    );
  }
  return (
    <TenantProvider tenant={membership}>
      <AppShell>
        <Suspense fallback={<Loading label={tx.app.loading} />}>
          <Routes>
            <Route index element={<FleetScreen />} />
            <Route path="flugzeug/:reg" element={<Navigate to="plan" replace />} />
            <Route path="flugzeug/:reg/plan" element={<PlanScreen mode="plan" />} />
            <Route path="flugzeug/:reg/liste" element={<PlanScreen mode="list" />} />
            <Route path="schaden/:id" element={<DamageDetailScreen />} />
            <Route path="suche" element={<SearchScreen />} />
            <Route
              path="eingang"
              element={
                <RequireRole min="editor">
                  <InboxScreen />
                </RequireRole>
              }
            />
            <Route path="profil" element={<ProfileScreen />} />
            <Route path="datenschutz" element={<TenantPrivacyScreen />} />
            <Route path="profil/meldungen" element={<MyReportsScreen />} />
            <Route path="hilfe" element={<HelpScreen />} />
            <Route path="hilfe/:topic" element={<HelpScreen />} />
            <Route
              path="admin/*"
              element={
                <RequireRole min="admin">
                  <AdminRoutes />
                </RequireRole>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </AppShell>
    </TenantProvider>
  );
}

/**
 * /demo, /demo/<persona> reached inside the running app (a hard load is
 * already handled by bootstrapSession before the router starts). Starts the
 * demo — never the network — and lands on the demo club's fleet. An unknown
 * persona is an ordinary not-found page.
 */
function DemoRoute() {
  const location = useLocation();
  const state = useSessionState();
  const entry = demoEntryFromUrl(location.pathname, location.search);
  const role = entry?.role ?? null;
  const active = role !== null && state.status === 'signed-in' && state.session.mode === 'demo' && state.session.demoRole === role;
  useEffect(() => {
    if (role && !active) startDemo(role);
  }, [role, active]);
  if (!entry) return <NotFound />;
  if (!active) return <Loading />;
  return <Navigate to={entry.path} replace />;
}

function LoginRoute() {
  const state = useSessionState();
  const location = useLocation();
  if (state.status === 'signed-in') {
    const from = (location.state as { from?: unknown } | null)?.from;
    return <Navigate to={typeof from === 'string' && from.startsWith('/') && !from.startsWith('//') ? from : '/'} replace />;
  }
  if (state.status === 'loading') return <Loading />;
  return <LoginScreen />;
}

const signedIn = (node: ReactNode) => <RequireSession>{node}</RequireSession>;

/**
 * /hilfe works without a session too (the login page links to it, and a
 * code that does not arrive is a question asked before signing in). Signed
 * in, it gets the usual frame; inside a club it lives at /v/<slug>/hilfe.
 */
function HelpRoute() {
  const state = useSessionState();
  if (state.status === 'loading') return <Loading />;
  if (state.status === 'signed-in') {
    return (
      <AppShell>
        <HelpScreen />
      </AppShell>
    );
  }
  return (
    <div className="login">
      <div className="login__sheet">
        <HelpScreen />
      </div>
      <Footer />
    </div>
  );
}
const shell = (node: ReactNode) => signedIn(<AppShell>{node}</AppShell>);

/**
 * A new page starts at the top. BrowserRouter keeps the scroll position across
 * route changes, so a link in the footer opened the next page at its bottom.
 * Only the path counts: changing the plan's ?view= must not jump.
 */
function ScrollToTopOnNavigate() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export function App() {
  return (
    <Suspense fallback={<Loading label={tx.app.loading} />}>
      <ScrollToTopOnNavigate />
      <Routes>
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/datenschutz" element={<PrivacyScreen />} />
        <Route path="/impressum" element={<ImpressumScreen />} />
        <Route path="/nutzungsbedingungen" element={<TermsScreen />} />
        <Route path="/verein-anmelden" element={<SignupScreen />} />
        <Route path="/hilfe" element={<HelpRoute />} />
        <Route path="/hilfe/:topic" element={<HelpRoute />} />
        <Route path="/demo/*" element={<DemoRoute />} />
        <Route path="/" element={signedIn(<HomeRedirect />)} />
        <Route path="/vereine" element={shell(<TenantPickerScreen />)} />
        <Route path="/profil" element={shell(<ProfileScreen />)} />
        <Route
          path="/plattform/*"
          element={shell(
            <RequirePlatformManager>
              <PlatformRoutes />
            </RequirePlatformManager>,
          )}
        />
        <Route path="/v/:slug/*" element={signedIn(<TenantRoute />)} />
        {['/flugzeug/*', '/schaden/*', '/suche', '/eingang', '/admin/*'].map((p) => (
          <Route key={p} path={p} element={signedIn(<LegacyRedirect />)} />
        ))}
        <Route path="*" element={shell(<NotFound />)} />
      </Routes>
    </Suspense>
  );
}
